// Controls
import { Menu, MenuButton, MenuGroup, MenuGroupHeader, MenuItem, MenuList, MenuPopover, MenuTrigger, Tooltip } from "@fluentui/react-components";
import { format } from "@fluentui/utilities";
import { BoardSplit, LinkMultipleBundled, LockShield } from "@ms/planner-generic-controls/lib/local/icons";
// Constants
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
// Styles
import { useConnectedPlansButtonStyles } from "./ConnectedPlansButton.styles";
// Utilities
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import * as React from "react";
const ComponentName = "ConnectedPlansButton";
const OverflowTooltipLabel = ({ className, dataTestId, displayName })=>{
    const [tooltipVisible, setTooltipVisible] = React.useState(false);
    const labelRef = React.useRef(null);
    return /*#__PURE__*/ React.createElement(Tooltip, {
        content: displayName,
        relationship: "description",
        withArrow: true,
        visible: tooltipVisible,
        onVisibleChange: (_event, data)=>{
            const label = labelRef.current;
            setTooltipVisible(data.visible && !!label && label.scrollWidth > label.clientWidth);
        }
    }, /*#__PURE__*/ React.createElement("span", {
        ref: labelRef,
        className: className,
        "data-testid": dataTestId
    }, displayName));
};
/**
 * Button for a plan connected to one or more master tasks. Shows a count of the
 * connected plans and opens a dropdown listing them on click.
 */ export const ConnectedPlansButton = ({ connectedPlans, onClickConnectedTask, onOpenConnectedPlansMenu })=>{
    const styles = useConnectedPlansButtonStyles();
    if (connectedPlans.length === 0) {
        return null;
    }
    const buttonText = String(connectedPlans.length);
    const buttonLabel = format(Strings.PlanHeader_ConnectedPlansButtonAriaLabel, connectedPlans.length);
    const getUnavailablePlanLabel = (displayName, reason)=>format(Strings.PlanHeader_ConnectedPlanUnavailableAccessibleName, displayName, reason);
    return /*#__PURE__*/ React.createElement(Menu, {
        positioning: {
            offset: 4
        },
        onOpenChange: (_event, data)=>{
            if (data.open) {
                onOpenConnectedPlansMenu?.();
            }
        }
    }, /*#__PURE__*/ React.createElement(MenuTrigger, {
        disableButtonEnhancement: true
    }, /*#__PURE__*/ React.createElement(Tooltip, {
        content: buttonLabel,
        relationship: "label"
    }, /*#__PURE__*/ React.createElement(MenuButton, {
        appearance: "subtle",
        icon: /*#__PURE__*/ React.createElement(LinkMultipleBundled, null),
        menuIcon: null,
        className: styles.button,
        "aria-label": buttonLabel,
        "data-testid": "connected-plans-button"
    }, buttonText))), /*#__PURE__*/ React.createElement(MenuPopover, {
        className: styles.menuPopover
    }, /*#__PURE__*/ React.createElement(MenuList, null, /*#__PURE__*/ React.createElement(MenuGroupHeader, {
        className: styles.headerItem,
        "data-testid": "connected-plans-header"
    }, Strings.PlanHeader_ConnectedPlansButtonTooltip), connectedPlans.map(({ id, displayName, status, tasks })=>{
        const connectedTasksHeaderId = `${ComponentName}-connected-tasks-header-${id}`;
        const isAccessible = status.state === StoreEntityState.Present && !status.isFetching;
        const unavailableReason = status.state === StoreEntityState.Inaccessible ? Strings.TaskHistory_NoAccessDescription : status.state === StoreEntityState.NotFound ? Strings.Errors_EvoPlanNotFoundErrorText : undefined;
        return isAccessible ? /*#__PURE__*/ React.createElement(Menu, {
            key: id,
            openOnHover: true,
            positioning: {
                position: "after",
                align: "top"
            }
        }, /*#__PURE__*/ React.createElement(MenuTrigger, {
            disableButtonEnhancement: true
        }, /*#__PURE__*/ React.createElement(MenuItem, {
            icon: /*#__PURE__*/ React.createElement(BoardSplit, null),
            className: styles.connectedTaskItem,
            content: {
                className: styles.connectedTaskItemContent
            },
            "data-testid": "connected-plans-menu-item"
        }, /*#__PURE__*/ React.createElement(OverflowTooltipLabel, {
            className: styles.menuItemLabel,
            dataTestId: "connected-plan-menu-item-label",
            displayName: displayName
        }))), /*#__PURE__*/ React.createElement(MenuPopover, {
            className: styles.menuPopover
        }, /*#__PURE__*/ React.createElement(MenuList, null, /*#__PURE__*/ React.createElement(MenuGroup, {
            "aria-labelledby": connectedTasksHeaderId
        }, /*#__PURE__*/ React.createElement(MenuGroupHeader, {
            id: connectedTasksHeaderId,
            className: styles.headerItem,
            "data-testid": "connected-tasks-header"
        }, Strings.PlanHeader_ConnectedTasksMenuHeader), tasks.map((task)=>/*#__PURE__*/ React.createElement(MenuItem, {
                key: task.contentLink.webUrl,
                className: styles.connectedTaskItem,
                content: {
                    className: styles.connectedTaskItemContent
                },
                onClick: ()=>onClickConnectedTask(task.contentLink),
                "data-testid": "connected-plan-task-menu-item"
            }, /*#__PURE__*/ React.createElement(OverflowTooltipLabel, {
                className: styles.menuItemLabel,
                dataTestId: "connected-task-menu-item-label",
                displayName: task.displayName
            }))))))) : /*#__PURE__*/ React.createElement(MenuItem, {
            key: id,
            icon: unavailableReason ? /*#__PURE__*/ React.createElement(LockShield, null) : undefined,
            className: styles.connectedTaskItem,
            content: {
                className: styles.connectedTaskItemContent
            },
            "data-testid": "connected-plans-menu-item",
            "aria-label": unavailableReason ? getUnavailablePlanLabel(displayName, unavailableReason) : undefined,
            disabled: true
        }, /*#__PURE__*/ React.createElement(OverflowTooltipLabel, {
            className: styles.menuItemLabel,
            dataTestId: "connected-plan-menu-item-label",
            displayName: displayName
        }));
    }))));
};
ConnectedPlansButton.displayName = ComponentName;
